import { useMemo, useState } from 'react';
import Modal from '../../ui/Modal';
import SecondaryButton from '../../ui/SecondaryButton';
import { useCarStoreContext } from '../store/car-store-provider';
import { CalcModel } from '../types';

export default function SwitchCar() {
  const allCarModels = useCarStoreContext((state) => state.allCarModels);
  const selectableTrimCodes = useCarStoreContext(
    (state) => state.selectableTrimCodes,
  );
  const replaceActiveCar = useCarStoreContext(
    (state) => state.replaceActiveCar,
  );
  const activeTrimCodes = useCarStoreContext((state) => state.activeTrimCodes);
  const isSwitchCarModalOpen = useCarStoreContext(
    (state) => state.isSwitchCarModalOpen,
  );
  const closeSwitchCarModal = useCarStoreContext(
    (state) => state.closeSwitchCarModal,
  );
  const carIndexToReplace = useCarStoreContext(
    (state) => state.carIndexToReplace,
  );

  const [selectedModel, setSelectedModel] = useState<CalcModel | null>(null);

  const otherActiveCarCodes = useMemo(() => {
    if (carIndexToReplace === null) {
      return [];
    }
    return activeTrimCodes.filter((_, index) => index !== carIndexToReplace);
  }, [activeTrimCodes, carIndexToReplace]);

  const filteredCarModels = useMemo(() => {
    if (!selectableTrimCodes.length || !allCarModels) {
      return [];
    }
    return allCarModels.filter((model) =>
      model.calculator_cars.some((car) =>
        selectableTrimCodes.includes(car.code),
      ),
    );
  }, [allCarModels, selectableTrimCodes]);

  const handleSelectCar = (newTrimCode: string) => {
    if (carIndexToReplace !== null) {
      replaceActiveCar(carIndexToReplace, newTrimCode);
    }
    handleCloseModal();
  };

  const handleCloseModal = () => {
    closeSwitchCarModal();
    setSelectedModel(null);
  };

  if (!isSwitchCarModalOpen) {
    return null;
  }

  return (
    <Modal clickOutside={handleCloseModal}>
      <div className="relative max-w-xl bg-white max-h-[90dvh] lg:max-h-[70dvh] p-8 flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">
            {selectedModel ? `Välj en ${selectedModel.name}` : 'Byt modell'}
          </h1>
        </div>

        <div className="overflow-y-auto flex flex-col min-w-96 gap-4">
          {!selectedModel ? (
            <ul className="grid gap-2">
              {filteredCarModels.map((model) => (
                <li key={model.id} className="grid">
                  <button
                    onClick={() => setSelectedModel(model)}
                    className="flex shadow m-1 px-3 py-4 rounded items-center gap-2 justify-between hover:underline hover:bg-kia-grey/50 transition-colors"
                  >
                    <img
                      className="w-28"
                      src={model.calculator_cars[0]?.image}
                      alt={model.name}
                    />
                    <p>{model.name}</p>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="grid gap-2">
              {selectedModel.calculator_cars
                .filter((car) => selectableTrimCodes.includes(car.code))
                .filter((car) => !otherActiveCarCodes.includes(car.code))
                .map((car) => (
                  <li className="grid" key={car.code}>
                    <button
                      onClick={() => handleSelectCar(car.code)}
                      className="flex shadow m-1 px-3 py-4 rounded items-center gap-2 justify-between hover:underline hover:bg-kia-grey/50 transition-colors"
                    >
                      <img className="w-28" src={car.image} alt={car.name} />
                      <p>{car.name}</p>
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </div>

        <div className="grid gap-4">
          {selectedModel && (
            <SecondaryButton
              onClick={() => setSelectedModel(null)}
              className="w-full"
            >
              Tillbaka
            </SecondaryButton>
          )}
          <SecondaryButton onClick={handleCloseModal} className="w-full">
            Avbryt
          </SecondaryButton>
        </div>
      </div>
    </Modal>
  );
}
